import React, { useEffect } from "react"
import propTypes from "prop-types"
import { Alert, Button, Col, Form, Row, Card, CardBody } from "reactstrap"
import { Formik } from "formik"
import { useBillingForm } from "../hooks/useBillingForm"
import { validationSchema } from "../helpers/validationSchema"
import ReceiverSection from "./ReceiverSection"
import EmitterSection from "./EmitterSection"
import TicketSelectorList from "./TicketSelectorList"
import ProductsTable from "./ProductsTable"
import BillingSummary from "./BillingSummary"

const NewBillingForm = ({ toggle }) => {
    const {
        error,
        isLoading,
        ticketSelectors,
        products,
        subtotal,
        discount,
        taxBreakdown,
        total,
        initialValues,
        businessInfo,
        invoices,
        usoCfdiOptions,
        loadTicketOptions,
        loadCustomerOptions,
        handleTicketChange,
        handleAddSelector,
        handleRemoveSelector,
        handleSubmit,
        handleClientChange
    } = useBillingForm(toggle)

    return (
        // Contenedor principal del formulario de nueva factura
        <div>
            {error && <Alert color="danger" className="mb-3">{error}</Alert>}

            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
                    useEffect(() => {
                        if (businessInfo.data?.results?.[0]) {
                            const business = businessInfo.data.results[0]
                            setFieldValue("emitterCompanyName", business.business_name || "")
                            setFieldValue("emitterRfc", business.rfc || "")
                            setFieldValue("emitterPostalCode", 
                                business.fiscal_zipcode || business.zip_code || ""
                            )

                            const emitterTaxRegimen =
                                business.tax_regimen_data
                                    ? `${business.tax_regimen_data.code} - ${business.tax_regimen_data.name}`
                                    : ""

                            setFieldValue("emitterFiscalRegimen", emitterTaxRegimen)
                        }
                    }, [businessInfo.data, setFieldValue])
                    
                    return (
                        <Form onSubmit={handleSubmit}>
                            <ReceiverSection
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                setFieldValue={setFieldValue}
                                handleClientChange={(e) =>
                                    handleClientChange(e.target.value, e.target.label, setFieldValue)
                                }
                                loadCustomerOptions={loadCustomerOptions}
                                usoCfdiOptions={usoCfdiOptions}
                            />

                            <EmitterSection
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                            />

                            <TicketSelectorList
                                ticketSelectors={ticketSelectors}
                                ticketsLoading={invoices.isLoading}
                                isLoading={isLoading}
                                handleTicketChange={handleTicketChange}
                                handleRemoveSelector={handleRemoveSelector}
                                handleAddSelector={handleAddSelector}
                                loadTicketOptions={loadTicketOptions}
                            />

                            <Card className="mb-3">
                                <CardBody className="p-3">
                                    <ProductsTable products={products} />
                                    <BillingSummary
                                        subtotal={subtotal}
                                        discount={discount}
                                        taxBreakdown={taxBreakdown}
                                        total={total}
                                    />
                                </CardBody>
                            </Card>

                            <Row className="mt-4">
                                <Col className="text-end">
                                    <Button 
                                        type="button" 
                                        color="secondary" 
                                        className="me-3" 
                                        onClick={toggle} 
                                        disabled={isLoading}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        color="success" 
                                        disabled={isSubmitting || isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="fa fa-spinner fa-spin me-2" />
                                                Procesando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save me-2" />
                                                Facturar
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )
                }}
            </Formik>
        </div>
    )
}

NewBillingForm.propTypes = {
    toggle: propTypes.func.isRequired,
}

export default NewBillingForm